/**
 * Currency conversion utilities for the Quote Editor
 * 
 * All quote prices are stored in SEK (base currency) and converted for display only.
 * This ensures data integrity while providing flexible currency display.
 */

/**
 * Convert price from SEK to target currency using exchange rates
 * @param {number} sekPrice - Price in SEK (base currency)
 * @param {string} targetCurrency - Target currency code ('SEK', 'EUR', 'USD')
 * @param {Object} exchangeRates - Exchange rates object from quote data
 * @returns {number} Converted price
 */
export const convertFromSEK = (sekPrice, targetCurrency, exchangeRates) => {
  if (!sekPrice || isNaN(sekPrice)) {
    return 0;
  }
  
  if (targetCurrency === 'SEK') {
    return sekPrice;
  }
  
  if (!exchangeRates || !exchangeRates.rates || !exchangeRates.rates[targetCurrency]) {
    console.warn(`No exchange rate found for ${targetCurrency}, showing SEK price`);
    return sekPrice;
  }
  
  const rate = exchangeRates.rates[targetCurrency];
  const convertedPrice = sekPrice * rate;
  
  // Round to 2 decimal places
  return Math.round(convertedPrice * 100) / 100;
};

/**
 * Get currency symbol for display
 * @param {string} currency - Currency code
 * @returns {string} Currency symbol or code
 */
export const getCurrencySymbol = (currency) => {
  const symbols = {
    'SEK': 'SEK',
    'EUR': '€',
    'USD': '$'
  };
  
  return symbols[currency] || currency;
};

/**
 * Get currency display name
 * @param {string} currency - Currency code
 * @returns {string} Currency display name
 */
export const getCurrencyDisplayName = (currency) => {
  const names = {
    'SEK': 'Swedish Krona',
    'EUR': 'Euro',
    'USD': 'US Dollar'
  };
  
  return names[currency] || currency;
};

/**
 * Check if exchange rates are available for currency conversion
 * @param {Object} exchangeRates - Exchange rates object
 * @param {string} targetCurrency - Target currency to check
 * @returns {boolean} True if conversion is possible
 */
export const canConvertToCurrency = (exchangeRates, targetCurrency) => {
  if (targetCurrency === 'SEK') {
    return true; // No conversion needed for base currency
  }
  
  return !!(exchangeRates && exchangeRates.rates && exchangeRates.rates[targetCurrency]);
};

/**
 * Get available currencies based on exchange rates
 * @param {Object} exchangeRates - Exchange rates object
 * @returns {Array} Array of available currency objects
 */
export const getAvailableCurrencies = (exchangeRates) => {
  const currencies = [
    { code: 'SEK', symbol: 'SEK', name: 'Swedish Krona' }
  ];
  
  if (exchangeRates && exchangeRates.rates) {
    if (exchangeRates.rates.EUR) {
      currencies.push({ code: 'EUR', symbol: '€', name: 'Euro' });
    }
    if (exchangeRates.rates.USD) {
      currencies.push({ code: 'USD', symbol: '$', name: 'US Dollar' });
    }
  }
  
  return currencies;
};
